import { CheckCircle2, ChevronDown, ChevronRight, Loader2, Wrench, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../../lib/utils';
import type { ToolCallBlock } from '../chat-content';

const STATUS_ICON: Record<NonNullable<ToolCallBlock['status']>, React.ReactNode> = {
  pending: <Loader2 className="size-3.5 animate-spin text-muted-foreground" />,
  success: <CheckCircle2 className="size-3.5 text-success" />,
  error: <XCircle className="size-3.5 text-destructive" />,
};

const STATUS_LABEL: Record<NonNullable<ToolCallBlock['status']>, string> = {
  pending: 'Running…',
  success: 'Done',
  error: 'Failed',
};

export function ToolCallBlockRenderer({ block }: { block: ToolCallBlock }) {
  const [open, setOpen] = React.useState(false);
  const status = block.status ?? 'success';
  const hasDetails = block.arguments !== undefined || block.result !== undefined;

  return (
    <div className="overflow-hidden rounded-lg border border-border text-sm">
      {/* Header — clickable if there are details */}
      <button
        type="button"
        disabled={!hasDetails}
        className={cn(
          'flex w-full items-center gap-2 bg-muted/50 px-3 py-2 text-left',
          hasDetails && 'hover:bg-muted/80 cursor-pointer',
          !hasDetails && 'cursor-default',
        )}
        onClick={() => hasDetails && setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Wrench className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate font-mono text-xs font-medium">{block.toolName}</span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          {STATUS_ICON[status]}
          {STATUS_LABEL[status]}
        </span>
        {hasDetails &&
          (open ? (
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          ))}
      </button>

      {/* Expandable body */}
      {open && hasDetails && (
        <div className="divide-y divide-border">
          {block.arguments !== undefined && (
            <div className="px-3 py-2.5 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Input
              </p>
              <pre className="overflow-x-auto font-mono text-xs leading-relaxed">
                {JSON.stringify(block.arguments, null, 2)}
              </pre>
            </div>
          )}
          {block.result !== undefined && (
            <div className="px-3 py-2.5 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Output
              </p>
              <pre className="overflow-x-auto font-mono text-xs leading-relaxed">
                {typeof block.result === 'string'
                  ? block.result
                  : JSON.stringify(block.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
